import { makeAutoObservable } from 'mobx'
import { type IIconsVector } from 'shared/ui'
import {
  type BillingPlan,
  basicMonthlyMaxCredits,
  basicAnnualMaxCredits,
} from 'entities/Billing/model/BillingPlan'
import type { BillingStore } from 'entities/Billing'
import { type BillingInterval } from 'entities/Billing/api/types'

export type BillingPeriod = 'monthly' | 'annually'

export type ISelectedPlanInfo = {
  id: string
  icon: IIconsVector
  name: string
  iconColor: string | undefined
  credits: number
  additionalCreditPrice: number
  interval: BillingInterval
}

const planIdExceptions = [
  'pro-monthly-250',
  'pro-monthly-750',
  'pro-yearly-3000',
  'pro-monthly-7500-2',
  'pro-monthly-7500-3',
  'pro-yearly-9000',
  'pro-yearly-90000_2',
]

export class PlansSelectorStore {
  constructor(readonly billingStore: BillingStore) {
    makeAutoObservable(this)
  }

  getMaxCreditsForInterval = (interval: BillingPeriod): number => {
    return interval === 'monthly' ? basicMonthlyMaxCredits : basicAnnualMaxCredits
  }

  getPlansList = (interval: BillingPeriod): BillingPlan[] => {
    const { billingPlansStore } = this.billingStore

    if (interval === 'monthly') {
      return billingPlansStore.monthlyPlans.filter((plan) => !planIdExceptions.includes(plan.id))
    }

    return billingPlansStore.annualPlans.filter((plan) => !planIdExceptions.includes(plan.id))
  }

  getBasicPlans = (interval: BillingPeriod): BillingPlan[] => {
    const maxCredits = this.getMaxCreditsForInterval(interval)
    return this.getPlansList(interval).filter((plan) => plan.credits <= maxCredits)
  }

  getProPlans = (interval: BillingPeriod): BillingPlan[] => {
    const maxCredits = this.getMaxCreditsForInterval(interval)
    return this.getPlansList(interval).filter((plan) => plan.credits > maxCredits)
  }
}
